"use client";

import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Copy,
  Loader2,
  Wand2,
  PartyPopper,
  ClipboardList,
  FileDown,
  Lock,
  ImageDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuthSession } from "@/lib/auth-client";
import { trackEvent } from "@/lib/track";
import { cn } from "@/lib/utils";
import { exportFortunesToPDF } from "@/lib/pdf-export";
import { buildShareCardUrl, shareOrDownloadCard } from "@/lib/share-card";
import { withClientId } from "@/lib/client-id";
import {
  extractQuota,
  extractQuotaLimits,
  type QuotaInfo,
  type QuotaLimits,
} from "@/lib/quota-client";
import { QuotaGateCard } from "@/components/QuotaGateCard";
import {
  EVENT_TYPES,
  EVENT_TONES,
  EVENT_QUANTITIES,
  FREE_EVENT_MAX_QUANTITY,
  isEventQuantityAllowed,
  type EventType,
  type EventTone,
} from "@/lib/prompts/event";

interface GeneratedFortune {
  message: string;
  luckyNumbers: number[];
  /** 服务端为这条正文签发的 HMAC,分享卡片必须带上它,见 lib/og-signature.ts。 */
  shareSig?: string;
}

function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={value === opt.value}
            className={cn(
              "rounded-xl border px-3 py-2 text-sm font-medium transition-all",
              value === opt.value
                ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/20 dark:text-amber-200"
                : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EventClient() {
  const { data: session } = useAuthSession();
  const isPremium = Boolean(session?.user?.isPremium);

  const [eventType, setEventType] = useState<EventType>("wedding");
  const [personalization, setPersonalization] = useState("");
  const [tone, setTone] = useState<EventTone>("sweet");
  const [quantity, setQuantity] = useState<number>(10);
  const [avoidDuplicates, setAvoidDuplicates] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<GeneratedFortune[]>([]);
  const [pdfLuckyNumbers, setPdfLuckyNumbers] = useState(false);
  const [pdfDate, setPdfDate] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [quotaGate, setQuotaGate] = useState<QuotaInfo | null>(null);
  // 限额数字随 429 一起来自服务端,不再读 NEXT_PUBLIC_ 镜像。
  const [quotaLimits, setQuotaLimits] = useState<QuotaLimits | undefined>(
    undefined,
  );
  const [quotaMessage, setQuotaMessage] = useState<string | undefined>();

  const generate = useCallback(async () => {
    if (!isEventQuantityAllowed(quantity, isPremium)) {
      toast.error(
        `Batches over ${FREE_EVENT_MAX_QUANTITY} are a Premium feature.`,
      );
      return;
    }
    setIsLoading(true);
    // Clear any previous gate up front: a stale card (e.g. pre-sign-in guest
    // numbers) left on screen next to a fresh non-429 error reads as "signing
    // in did nothing".
    setQuotaGate(null);
    setQuotaMessage(undefined);
    try {
      const res = await fetch("/api/generator", {
        method: "POST",
        headers: withClientId({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          mode: "event",
          source: "generator",
          params: { eventType, personalization, tone, quantity, avoidDuplicates },
        }),
      });
      // Bare .json() throws on the HTML error pages a CDN/edge can return,
      // which the outer catch would mislabel as "Network error".
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        // Daily limit: show an in-place card (results above are kept) instead
        // of a toast the user can miss.
        const quota = res.status === 429 ? extractQuota(json) : null;
        if (quota) {
          setQuotaGate(quota);
          setQuotaLimits(extractQuotaLimits(json));
          setQuotaMessage(
            typeof json?.error === "string" ? json.error : undefined,
          );
          return;
        }
        toast.error(json?.error || "Generation failed. Please try again.");
        return;
      }
      const fortunes: GeneratedFortune[] = json?.data?.fortunes ?? [];
      if (fortunes.length === 0) {
        toast.error("Nothing came back. Try again.");
        return;
      }
      // Reported on a successful reveal only. Generator modes have no local
      // fallback (failures surface as a toast and return early), so
      // is_fallback is structurally false here.
      trackEvent("cookie_cracked", {
        source: "generator",
        is_fallback: false,
      });
      setResults(fortunes);
      setQuotaGate(null);
      if (avoidDuplicates && fortunes.length < quantity) {
        toast.info(
          `${fortunes.length} unique messages (duplicates removed).`,
        );
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [eventType, personalization, tone, quantity, avoidDuplicates, isPremium]);

  const copyOne = useCallback((text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  }, []);

  const shareImage = useCallback(
    async (fortune: GeneratedFortune) => {
      const url = buildShareCardUrl({
        message: fortune.message,
        luckyNumbers: fortune.luckyNumbers,
        mode: "event",
        signature: fortune.shareSig,
      });
      const result = await shareOrDownloadCard(url, fortune.message);
      if (result === "downloaded") toast.success("Image saved");
    },
    [],
  );

  const copyAll = useCallback(() => {
    const all = results.map((r) => r.message).join("\n");
    navigator.clipboard?.writeText(all).then(
      () => toast.success(`Copied ${results.length} messages`),
      () => toast.error("Copy failed"),
    );
  }, [results]);

  const exportPdf = useCallback(async () => {
    if (results.length === 0) return;
    if (!isPremium) {
      toast.error("PDF export is a Premium feature.");
      return;
    }
    setIsExporting(true);
    try {
      const eventLabel =
        EVENT_TYPES.find((e) => e.value === eventType)?.label ?? "Event";
      await exportFortunesToPDF(results, {
        title: `${eventLabel} Fortunes`,
        includeLuckyNumbers: pdfLuckyNumbers,
        includeDate: pdfDate,
      });
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setIsExporting(false);
    }
  }, [results, eventType, pdfLuckyNumbers, pdfDate, isPremium]);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)]">
      {/* Parameter panel */}
      <div className="space-y-6 rounded-2xl border border-slate-200 bg-white/70 p-6 backdrop-blur dark:border-slate-700 dark:bg-slate-900/60">
        <Segmented
          label="Event Type"
          options={EVENT_TYPES}
          value={eventType}
          onChange={setEventType}
        />

        <div>
          <label
            htmlFor="event-personalization"
            className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Personalization{" "}
            <span className="text-xs font-normal text-slate-400">
              (names, dates, hobbies…)
            </span>
          </label>
          <textarea
            id="event-personalization"
            value={personalization}
            onChange={(e) => setPersonalization(e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="e.g. Sarah & Tom, met in Paris, love hiking"
            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>

        <Segmented
          label="Tone"
          options={EVENT_TONES}
          value={tone}
          onChange={setTone}
        />

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            Quantity
          </p>
          <div className="flex flex-wrap gap-2">
            {EVENT_QUANTITIES.map((q) => {
              const locked = !isEventQuantityAllowed(q, isPremium);
              return (
                <button
                  key={q}
                  type="button"
                  onClick={() => {
                    if (locked) {
                      toast.error(
                        `Batches over ${FREE_EVENT_MAX_QUANTITY} are a Premium feature.`,
                      );
                      return;
                    }
                    setQuantity(q);
                  }}
                  aria-pressed={quantity === q}
                  title={locked ? "Premium feature" : undefined}
                  className={cn(
                    "flex items-center gap-1 rounded-xl border px-3 py-2 text-sm font-medium transition-all",
                    locked && "text-slate-400",
                    quantity === q
                      ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/20 dark:text-amber-200"
                      : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                  )}
                >
                  {q}
                  {locked && <Lock className="h-3 w-3" />}
                </button>
              );
            })}
          </div>
          {!isPremium && (
            <p className="mt-2 text-xs text-slate-400">
              Batches over {FREE_EVENT_MAX_QUANTITY} unlock with Premium.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label
            htmlFor="avoid-dupes"
            className="text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Avoid duplicates
          </label>
          <Switch
            id="avoid-dupes"
            checked={avoidDuplicates}
            onCheckedChange={setAvoidDuplicates}
          />
        </div>

        <Button
          onClick={generate}
          disabled={isLoading}
          className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Writing {quantity} messages…
            </>
          ) : (
            <>
              <Wand2 className="mr-2 h-4 w-4" />
              Generate {quantity} Messages
            </>
          )}
        </Button>
      </div>

      {/* Results */}
      <div className="min-h-[200px]">
        {quotaGate && (
          <QuotaGateCard
            quota={quotaGate}
            limits={quotaLimits}
            message={quotaMessage}
            className="mb-4"
          />
        )}

        {results.length === 0 && !isLoading && !quotaGate && (
          <div className="flex h-full min-h-[200px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-400 dark:border-slate-700">
            <PartyPopper className="mb-3 h-8 w-8" />
            <p>Describe the event and generate a batch of fortunes.</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500">
              {results.length} message{results.length === 1 ? "" : "s"}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                <Checkbox
                  checked={pdfLuckyNumbers}
                  onCheckedChange={(v) => setPdfLuckyNumbers(v === true)}
                />
                Lucky #s
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                <Checkbox
                  checked={pdfDate}
                  onCheckedChange={(v) => setPdfDate(v === true)}
                />
                Date
              </label>
              <Button variant="outline" size="sm" onClick={copyAll} className="rounded-lg">
                <ClipboardList className="mr-1.5 h-4 w-4" />
                Copy all
              </Button>
              <Button
                size="sm"
                onClick={exportPdf}
                disabled={isExporting}
                title={isPremium ? undefined : "Premium feature"}
                className={cn(
                  "rounded-lg",
                  isPremium
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
                    : "bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-400",
                )}
              >
                {isExporting ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : isPremium ? (
                  <FileDown className="mr-1.5 h-4 w-4" />
                ) : (
                  <Lock className="mr-1.5 h-4 w-4" />
                )}
                Export PDF
              </Button>
            </div>
          </div>
        )}

        <ul className="space-y-2 list-none p-0 m-0">
          <AnimatePresence>
            {results.map((fortune, index) => (
              <motion.li
                key={`${index}-${fortune.message.slice(0, 16)}`}
                initial={{ opacity: 0, y: -8, scaleY: 0.7 }}
                animate={{ opacity: 1, y: 0, scaleY: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  delay: Math.min(index * 0.03, 0.6),
                  duration: 0.25,
                  ease: "easeOut",
                }}
                style={{ transformOrigin: "top" }}
                className="group relative flex items-start gap-3 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-3 pr-16 dark:border-amber-500/30 dark:from-slate-800 dark:to-slate-800/60"
              >
                <span className="mt-0.5 text-xs font-medium text-amber-500">
                  {index + 1}
                </span>
                <p className="flex-1 font-serif text-sm text-slate-800 dark:text-slate-100">
                  {fortune.message}
                </p>
                <div className="absolute right-2 top-2 flex items-center opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => copyOne(fortune.message)}
                    aria-label="Copy message"
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-white/60 hover:text-amber-600 dark:hover:bg-slate-700/60"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shareImage(fortune)}
                    aria-label="Share as image"
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-white/60 hover:text-amber-600 dark:hover:bg-slate-700/60"
                  >
                    <ImageDown className="h-4 w-4" />
                  </button>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </div>
    </div>
  );
}
